import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CellRendererProps } from '../../../DataTable.types';
import { truncateByCharacters } from '../../../../../../utils/utils';
import Tooltip from '../../../../Tooltip/Tooltip';
import CaretIcon from '../../../../../../assets/Svg/CaretIcon';
import { COLORS } from '../../../../../../utils/constants/colors';
import MultiSelectWithDisabledOptions from '../../../../MultiSelectDropdown/MultiSelectWithDisabledOptions';
import { useOutsideClick } from '../../../../../../utils/constants/hooks';
import styles from './MultiSelectDropdownCellRenderer.module.scss';
import { useFixedDropdownInModal } from '../../../../../../utils/customHooks';
import Button from '../../../../Button/Button';

export interface UpdateMultiSelectDropdownProps {
    row?: any;
    column?: any;
    selectedValues?: string[];
}

interface Option {
    label: string;
    value: string;
}

export const MultiSelectDropdownCellRenderer: React.FC<CellRendererProps> = ({ value, row, column, dynamicProps }) => {
    const { updateMultiSelectDropdown, isDisabled, isConfigTableModalDropDown = false } = dynamicProps ?? {};
    const { dropdownOptions } = column;

    // Track selected values (should be an array from the row data)
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectedValuesRef = useRef<string[]>(selectedValues);
    const isDropdownOpenRef = useRef<boolean>(isDropdownOpen);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const initialValuesRef = useRef<string[]>([]); // Track initial values when dropdown opens

    // Helper function to check if values have changed
    const hasValuesChanged = useCallback(() => {
        return (
            initialValuesRef.current.length !== selectedValuesRef.current.length ||
            !initialValuesRef.current.every((val) => selectedValuesRef.current.includes(val)) ||
            !selectedValuesRef.current.every((val) => initialValuesRef.current.includes(val))
        );
    }, []);

    const dropdownPosition = useFixedDropdownInModal({
        isEnabled: isConfigTableModalDropDown,
        isOpen: isDropdownOpen,
        triggerRef: containerRef,
        dropdownRef,
        onClose: () => {
            setIsDropdownOpen(false);

            if (hasValuesChanged()) {
                updateMultiSelectDropdown({
                    row,
                    column,
                    selectedValues: selectedValuesRef.current,
                });
            }
        },
    });

    // Initialize selected values from the row data
    useEffect(() => {
        if (Array.isArray(value)) {
            setSelectedValues(value);
        } else if (value) {
            // Handle single value as array
            setSelectedValues([value]);
        } else {
            setSelectedValues([]);
        }
    }, [value]);

    useEffect(() => {
        selectedValuesRef.current = selectedValues;
    }, [selectedValues]);

    useEffect(() => {
        const wasOpen = isDropdownOpenRef.current;
        isDropdownOpenRef.current = isDropdownOpen;
        // Store initial values only when dropdown first opens (not on subsequent changes)
        if (isDropdownOpen && !wasOpen) {
            initialValuesRef.current = [...selectedValues];
        }
    }, [isDropdownOpen, selectedValues]);

    // Close dropdown when clicking outside and save changes (only if values changed)
    useOutsideClick(containerRef, () => {
        if (isDropdownOpenRef.current) {
            setIsDropdownOpen(false);

            if (hasValuesChanged() && updateMultiSelectDropdown) {
                updateMultiSelectDropdown({
                    row,
                    column,
                    selectedValues: selectedValuesRef.current,
                });
            }
        }
    });

    const handleToggleSelection = (newSelected: string[]) => {
        setSelectedValues(newSelected);
    };

    // Convert dropdownOptions object to array format for MultiSelectWithDisabledOptions
    const allOptions: Option[] = useMemo(() => {
        if (!dropdownOptions) return [];
        return Object.values(dropdownOptions).map((option: any) => ({
            label: option.label,
            value: option.value,
        }));
    }, [dropdownOptions]);

    // Get selected option objects with their styling
    const selectedOptions = useMemo(() => {
        if (!dropdownOptions || !selectedValues.length) return [];
        return selectedValues.map((val) => dropdownOptions[val]).filter(Boolean);
    }, [selectedValues, dropdownOptions]);

    if (!dropdownOptions) {
        return <div style={{ color: COLORS.GREY }}>-</div>;
    }

    if (!updateMultiSelectDropdown) {
        return null;
    }

    const firstName = selectedOptions[0]?.label ? truncateByCharacters(selectedOptions[0].label, 20) : '';
    const remainingOptions = selectedOptions.slice(1);
    const firstOption = selectedOptions[0];

    return (
        <div ref={containerRef}>
            <Button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (isDisabled) return;
                    setIsDropdownOpen((prev) => !prev);
                }}
                style={{
                    appearance: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    whiteSpace: 'nowrap',
                    border: 'none',
                    borderRadius: '2.75rem',
                    padding: '0.25rem 0.625rem 0.125rem',
                    width: 'fit-content',
                    font: 'inherit',
                    height: '1.75rem',
                    boxSizing: 'border-box',
                    marginTop: 0,
                    backgroundColor: firstOption?.backgroundColor || '#f5f5f5',
                    color: firstOption?.labelColor || '#000000',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.6 : 1,
                }}
                textstyle={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}
                text={
                    <>
                        {selectedOptions.length > 0 ? (
                            <Tooltip
                                direction="bottom"
                                style={{ width: 'fit-content', left: '-30px' }}
                                usePortal={isConfigTableModalDropDown}
                                content={
                                    <ul style={{ margin: 0, paddingLeft: '1em' }}>
                                        {selectedOptions.map((option, index) => (
                                            <li key={index}>{option.label}</li>
                                        ))}
                                    </ul>
                                }
                            >
                                <span className={styles.MultiSelectText} style={{ color: firstOption?.labelColor || '#000000' }}>
                                    {firstName}
                                </span>
                                {remainingOptions.length > 0 && <span className={styles.More}> +{remainingOptions.length} more</span>}
                            </Tooltip>
                        ) : (
                            <span className={styles.MultiSelectText} style={{ color: firstOption?.labelColor || '#b5b5b5' }}>
                                Select
                            </span>
                        )}
                        {!isDisabled && (
                            <div
                                className={clsx(styles.CaretWrapper, {
                                    [styles.CaretWrapperOpen]: isDropdownOpen,
                                })}
                            >
                                <CaretIcon colour={COLORS.GREY} height="13" width="13" />
                            </div>
                        )}
                    </>
                }
            />

            {isDropdownOpen && !isDisabled && (
                <div
                    ref={dropdownRef}
                    className={clsx(styles.MultiSelectDropdownCellRenderWrapper, {
                        [styles.isConfigTableModalDropDown]: isConfigTableModalDropDown,
                    })}
                    style={
                        isConfigTableModalDropDown
                            ? {
                                  position: 'fixed',
                                  top: `${dropdownPosition.top}px`,
                                  left: `${dropdownPosition.left}px`,
                                  width: `${dropdownPosition.width}px`,
                                  maxHeight: '320px',
                              }
                            : undefined
                    }
                >
                    <MultiSelectWithDisabledOptions
                        options={allOptions}
                        disabledOptions={[]}
                        selectedItems={selectedValues}
                        placeholder=""
                        toggleSelection={handleToggleSelection}
                        showInput={false}
                        dropdownStyle={
                            isConfigTableModalDropDown
                                ? { width: '300px', left: '0px', maxHeight: '225px' }
                                : { width: '300px', left: '0px' }
                        }
                        showAddButton={true}
                        onAddClick={() => {
                            try {
                                if (hasValuesChanged()) {
                                    updateMultiSelectDropdown({
                                        row,
                                        column,
                                        selectedValues: selectedValuesRef.current,
                                    });
                                }
                            } catch (err) {}
                        }}
                    />
                </div>
            )}
        </div>
    );
};
